import styles from './EmissionCalculatorWdht.module.scss';
import React, { useEffect, useState } from 'react';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import { Logo } from '@/components/Logo/Logo.tsx';
import { Link } from 'react-router-dom';
import { externalLinks } from '@/constants/externalLinks.ts';
import FormikAutocompleteAirportsInput from '@/components/Formik/FormikAutocompleteAirportsInput.tsx';
import { FieldArray, Form, FormikProvider, useFormik } from 'formik';
import CheckmarkIcon from '@/assets/Icons/Check-Mark.svg?react';
import Button from '@/components/ui/Button/Button.tsx';
import classNames from 'classnames';
import breakpoints from '@/constants/breakpoints.ts';
import { getEmissionCalculation } from '@/api/emissionCalculator.ts';
import { useMutation } from '@tanstack/react-query';
import LightIcon from '@/assets/Icons/Light.svg?react';
import MinusIcon from '@/assets/Icons/Minus.svg?react';
import PlusIcon from '@/assets/Icons/Plus.svg?react';
import { clamp } from 'lodash';
import { useCountUp } from 'use-count-up';
import { validationSchemaEmissionCalculator } from '@/validations/emissionCalculator.ts';
import AirportData = App.Data.AirportData;
import GetEmissionCalculationFlightsRequest = App.Data.Request.GetEmissionCalculationFlightsRequest;
import CopyIcon from '@/assets/Icons/Copy.svg?react';
import CheckIcon from '@/assets/Icons/Check-Mark.svg?react';

interface AirportDataExtended extends Omit<AirportData, 'id'> {
	id: number | undefined;
}

type FlightInput = {
	origin?: AirportDataExtended | null;
	destination?: AirportDataExtended | null;
};

type FormValues = {
	flights: FlightInput[];
	flightWay: 'one-way' | 'round-way';
	bookingClass: 'first' | 'premium_economy' | 'economy' | 'business' | undefined;
};

type SquakeFlightClass = 'first' | 'premium_economy' | 'economy' | 'business';

const formatCompact = (num: number) =>
	new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(num);

const Animated = ({ end, widthCh = 6, decimals = 0 }: { end: number; widthCh?: number; decimals?: number }) => {
	const { value } = useCountUp({
		isCounting: true,
		end,
		duration: 0.75,
		decimalPlaces: decimals,
	});

	return (
		<p className={styles.numeric} style={{ width: `${widthCh}ch` }}>
			{formatCompact(Number(value))}
		</p>
	);
};

const EmissionCalculator: React.FC = () => {
	const squakeSafPricePerTonne = 475;
	const [range1Value, setRange1Value] = useState<number>(0);
	const [safCostPercentage, setSafCostPercentage] = useState<number>(0);
	const [searchedRoute, setSearchedRoute] = useState<{ origin: string; destination: string } | undefined>(undefined);
	const [searchedBookingClass, setSearchedBookingClass] = useState<SquakeFlightClass | undefined>(undefined);
	const [searchedFlightWay, setSearchedFlightWay] = useState<string | undefined>(undefined);
	const [seed, setSeed] = useState(0);
	const [showSuccessCheckmark, setShowSuccessCheckmark] = useState(false);
	const [linkToCalculator, setLinkToCalculator] = useState('');
	const [initialCarbon, setInitialCarbon] = useState<number>(0);
	const [initialYearly, setInitialYearly] = useState<number>(0);
	const [initialSafCost, setInitialSafCost] = useState<number>(0);
	const [tempRange, setTempRange] = useState(range1Value);

	const { mutate, data, isPending, isError, isSuccess } = useMutation({
		mutationFn: getEmissionCalculation,
		onSuccess: () => setSeed(s => s + 1),
	});

	const formik = useFormik<FormValues>({
		initialValues: {
			flights: [{ origin: { id: undefined, name: undefined }, destination: { id: undefined, name: undefined } }],
			flightWay: 'round-way',
			bookingClass: undefined,
		},
		enableReinitialize: true,
		validationSchema: validationSchemaEmissionCalculator,
		onSubmit: async values => {
			setSearchedRoute({
				origin: values.flights[0].origin?.name || '',
				destination: values.flights[0].destination?.name || '',
			});
			setSearchedBookingClass(values.bookingClass);
			setSearchedFlightWay(values.flightWay);
			setRange1Value(10);
			setTempRange(10);
			setSafCostPercentage(10);

			const payload: GetEmissionCalculationFlightsRequest = {
				items: values.flights.flatMap(flight => {
					const leg = {
						origin: flight.origin?.name?.match(/\(([^)]+)\)/)?.[1] || '',
						destination: flight.destination?.name?.match(/\(([^)]+)\)/)?.[1] || '',
					};
					return values.flightWay === 'round-way'
						? [leg, { origin: leg.destination, destination: leg.origin }]
						: [leg];
				}),
				bookingClass: values.bookingClass || 'economy',
			};

			mutate(payload);
		},
	});

	const copyToClipboard = () => {
		navigator.clipboard.writeText(linkToCalculator);
		setShowSuccessCheckmark(true);
		setTimeout(() => setShowSuccessCheckmark(false), 3000);
	};

	const buildShareUrl = (values: FormValues, carbonQuantity: number, yearlyEmission: number, safCost: number) => {
		const params = new URLSearchParams({
			from: values.flights[0].origin?.name || '',
			to: values.flights[0].destination?.name || '',
			way: values.flightWay,
			class: values.bookingClass || 'economy',
			trips: range1Value.toString(),
			saf: safCostPercentage.toString(),
			carbon: carbonQuantity.toString(),
			yearly: yearlyEmission.toString(),
			safCost: safCost.toString(),
		});
		return `${window.location.origin}/emission-calculator?${params.toString()}`;
	};

	useEffect(() => {
		const sendHeight = () => {
			const height = document.documentElement.scrollHeight;
			window.parent.postMessage({ height }, '*');
		};

		const ro = new ResizeObserver(sendHeight);
		ro.observe(document.body);
		sendHeight();
		window.addEventListener('resize', sendHeight);
		return () => {
			ro.disconnect();
			window.removeEventListener('resize', sendHeight);
		};
	}, []);

	useEffect(() => {
		const qs = new URLSearchParams(window.location.search);
		const from = qs.get('from') || '';
		const to = qs.get('to') || '';
		const way = (qs.get('way') as 'one-way' | 'round-way') || 'round-way';
		const bookingClass = (qs.get('class') as SquakeFlightClass) || undefined;
		const trips = Number(qs.get('trips') || 0);
		const saf = Number(qs.get('saf') || 0);
		const carbon = Number(qs.get('carbon') || 0);
		const yearly = Number(qs.get('yearly') || 0);
		const safCost = Number(qs.get('safCost') || 0);

		if (!Number.isNaN(trips)) setRange1Value(trips);
		if (!Number.isNaN(trips)) setTempRange(trips);
		if (!Number.isNaN(saf)) setSafCostPercentage(saf);
		if (!Number.isNaN(carbon)) setInitialCarbon(carbon);
		if (!Number.isNaN(yearly)) setInitialYearly(yearly);
		if (!Number.isNaN(safCost)) setInitialSafCost(safCost);

		if (from || to || bookingClass || way) {
			formik.setValues({
				flights: [
					{
						origin: from ? { id: 1, name: `${from}` } : { id: undefined, name: undefined },
						destination: to ? { id: 2, name: `${to}` } : { id: undefined, name: undefined },
					},
				],
				flightWay: way,
				bookingClass,
			});

			if (carbon && yearly && safCost) {
				setSearchedRoute({ origin: `${from} (${from})`, destination: `${to} (${to})` });
				setSearchedBookingClass(bookingClass);
				setSearchedFlightWay(way);
				setSeed(s => s + 1);
			}
		}
	}, []);

	useEffect(() => {
		if (isSuccess && data?.carbonQuantity) {
			const carbonQuantity = data.carbonQuantity;
			const yearlyEmission = range1Value * (carbonQuantity / 1000);
			const safCost = yearlyEmission * (safCostPercentage / 100) * squakeSafPricePerTonne;
			setLinkToCalculator(buildShareUrl(formik.values, carbonQuantity, yearlyEmission, safCost));
		}
	}, [isSuccess, data, range1Value, safCostPercentage, formik.values]);

	function formatBookingClass(value: SquakeFlightClass): string {
		const mapping: Record<typeof value, string> = {
			economy: 'Economy',
			business: 'Business',
			premium_economy: 'Premium',
			first: 'First',
		};
		return mapping[value];
	}

	const carbonQuantity = data?.carbonQuantity ?? initialCarbon;
	const yearlyEmission = range1Value * (carbonQuantity / 1000) || initialYearly;
	const safCost =
		range1Value * (carbonQuantity / 1000) * (safCostPercentage / 100) * squakeSafPricePerTonne || initialSafCost;
	const finalCarbonStr = carbonQuantity.toLocaleString('de-DE', { maximumFractionDigits: 2 });
	const finalYearlyStr = yearlyEmission.toLocaleString('de-DE', { maximumFractionDigits: 2 });
	const finalSafStr = safCost.toLocaleString('de-DE', { maximumFractionDigits: 2 });

	return (
		<div className={styles.gwEmissionCalculatorWrapper}>
			<Link to={externalLinks.hubspot} target="_blank" className={styles.logoWrapper}>
				<Logo className={styles.logo} />
			</Link>
			<FlexBlock columnGap="80px" rowGap="60px" className={styles.columns}>
				<div>
					<FlexBlock
						justifyContent="space-between"
						alignItems="center"
						columnGap="20px"
						className={styles.header}
					>
						<FlexBlock flexDirection="column">
							<h2>See the cost of reducing your flight emissions</h2>
							<p>
								Calculate your flight emissions, and see the market cost of Sustainable Aviation Fuel to
								reduce them
							</p>
						</FlexBlock>
					</FlexBlock>
					<div className={classNames(styles.step, styles.stepOne)}>
						<FormikProvider value={formik}>
							<Form className={styles.form}>
								<FieldArray
									name="flights"
									render={() => (
										<>
											<FlexBlock columnGap="6px" className={styles.stepSubheader}>
												<span>Step 1</span> <p>Search for a typical business route</p>
											</FlexBlock>
											{formik.values.flights.map((_, index) => (
												<FlexBlock
													key={index}
													columnGap="20px"
													rowGap="12px"
													alignItems="flex-start"
													flexDirection={{
														[breakpoints.zero]: 'column',
														[breakpoints.tabletWide]: 'row',
													}}
													className={styles.flightRow}
												>
													<FormikAutocompleteAirportsInput
														name={`flights.${index}.origin`}
														label="From"
														placeholder="Search by city or airport"
														disabled={isPending}
														className={styles.placeField}
														additionalSpinnerStyleClass={styles.airportsFieldSpinnerWrapper}
													/>
													<FlexBlock
														columnGap={{
															[breakpoints.zero]: '6px',
															[breakpoints.tabletWide]: '20px',
														}}
														alignItems="flex-start"
														flex={1}
													>
														<FormikAutocompleteAirportsInput
															name={`flights.${index}.destination`}
															label="To"
															placeholder="Search by city or airport"
															disabled={isPending}
															className={styles.placeField}
															additionalSpinnerStyleClass={
																styles.airportsFieldSpinnerWrapper
															}
														/>
													</FlexBlock>
												</FlexBlock>
											))}
										</>
									)}
								/>

								<FlexBlock columnGap="24px" className={styles.radioButtons}>
									<FlexBlock alignItems="center" columnGap="10px">
										<div className={styles.inputWrapper}>
											<input
												type="radio"
												id="oneWay"
												name="flightWay"
												value="one-way"
												checked={formik.values.flightWay === 'one-way'}
												onChange={formik.handleChange}
												disabled={isPending}
												className={styles.radioBttn}
											/>
											<CheckmarkIcon />
										</div>
										<label
											htmlFor="oneWay"
											className={classNames(
												formik.values.flightWay === 'one-way' && styles.checked,
											)}
										>
											One way
										</label>
									</FlexBlock>

									<FlexBlock alignItems="center" columnGap="10px">
										<div className={styles.inputWrapper}>
											<input
												type="radio"
												id="roundWay"
												name="flightWay"
												value="round-way"
												checked={formik.values.flightWay === 'round-way'}
												onChange={formik.handleChange}
												disabled={isPending}
												className={styles.radioBttn}
											/>
											<CheckmarkIcon />
										</div>
										<label
											htmlFor="roundWay"
											className={classNames(
												formik.values.flightWay === 'round-way' && styles.checked,
											)}
										>
											Round-trip
										</label>
									</FlexBlock>
								</FlexBlock>
								<FlexBlock flexDirection="column" rowGap="13px" className={styles.bookingClass}>
									<p>Travel class</p>
									<div className={styles.buttonsWrapper}>
										<button
											type="button"
											onClick={() => formik.setFieldValue('bookingClass', 'economy')}
											className={classNames({
												[styles.checked]: formik.values.bookingClass === 'economy',
											})}
										>
											Economy
										</button>
										<button
											type="button"
											onClick={() => formik.setFieldValue('bookingClass', 'premium_economy')}
											className={classNames({
												[styles.checked]: formik.values.bookingClass === 'premium_economy',
											})}
										>
											Premium
										</button>
										<button
											type="button"
											onClick={() => formik.setFieldValue('bookingClass', 'business')}
											className={classNames({
												[styles.checked]: formik.values.bookingClass === 'business',
											})}
										>
											Business
										</button>
										<button
											type="button"
											onClick={() => formik.setFieldValue('bookingClass', 'first')}
											className={classNames({
												[styles.checked]: formik.values.bookingClass === 'first',
											})}
										>
											First class
										</button>
									</div>
									{formik.touched.bookingClass && formik.errors.bookingClass && (
										<div className={styles.errorMsg}>{formik.errors.bookingClass}</div>
									)}
								</FlexBlock>
								<Button
									type="submit"
									color="darkGreen"
									loading={isPending}
									disabled={isPending}
									className={styles.submitBttn}
									aria-label="Search"
								>
									Calculate
								</Button>

								{isError && (
									<p className={styles.errorMessage}>Something went wrong. Please try again.</p>
								)}

								{(data?.carbonQuantity || !!initialCarbon) && (
									<FlexBlock
										justifyContent="space-between"
										alignItems="center"
										columnGap="20px"
										className={styles.emissionInfo}
									>
										<FlexBlock flexDirection="column">
											<p>This trip emits {(carbonQuantity / 1000).toFixed(1)} tonnes of CO₂</p>
											<p>
												{searchedRoute?.origin} to {searchedRoute?.destination} •{' '}
												{searchedBookingClass && formatBookingClass(searchedBookingClass)} Class
												• {searchedFlightWay === 'one-way' ? 'One way' : 'Round-trip'}
											</p>
										</FlexBlock>
										<div className={styles.iconWrapper}>
											<LightIcon />
										</div>
									</FlexBlock>
								)}
							</Form>
						</FormikProvider>
					</div>
					<div className={classNames(styles.step, styles.stepTwo)}>
						<FlexBlock columnGap="6px" className={styles.stepSubheader}>
							<span>Step 2</span> <p>Average amount of yearly trips</p>
						</FlexBlock>
						<div className={styles.rangeWrapper}>
							<span className={styles.subheader}># of trips like this per year</span>
							<FlexBlock columnGap="40px">
								<input
									type="range"
									min="0"
									max="500"
									value={tempRange}
									onChange={e => setTempRange(Number(e.target.value))}
									onMouseUp={() => setRange1Value(tempRange)}
									onTouchEnd={() => setRange1Value(tempRange)}
									step="1"
									disabled={isPending}
									className={styles.range}
									aria-label="Trips per year"
								/>
								<input
									type="number"
									min="0"
									max="500"
									value={tempRange}
									onChange={e => {
										const raw = e.target.value;
										const normalized = raw.replace(/^0+/, '');
										const num = clamp(Number(normalized), 0, 500);

										setTempRange(num);
										setRange1Value(num);
									}}
									disabled={isPending}
									className={styles.rangeInputNumber}
								/>
							</FlexBlock>
						</div>
					</div>
					<div className={classNames(styles.step, styles.stepThree)}>
						<FlexBlock columnGap="6px" className={styles.stepSubheader}>
							<span>Step 3</span>{' '}
							<p>Choose how many % CO2 you want to reduce with Sustainable Aviation Fuel</p>
						</FlexBlock>
						<div className={styles.buttonsWrapper}>
							<button
								onClick={() => setSafCostPercentage(0)}
								className={classNames({ [styles.checked]: safCostPercentage === 0 })}
							>
								0%
							</button>
							<button
								onClick={() => setSafCostPercentage(10)}
								className={classNames({ [styles.checked]: safCostPercentage === 10 })}
							>
								10%
							</button>
							<button
								onClick={() => setSafCostPercentage(25)}
								className={classNames({ [styles.checked]: safCostPercentage === 25 })}
							>
								25%
							</button>
							<button
								onClick={() => setSafCostPercentage(50)}
								className={classNames({ [styles.checked]: safCostPercentage === 50 })}
							>
								50%
							</button>
							<button
								onClick={() => setSafCostPercentage(100)}
								className={classNames({ [styles.checked]: safCostPercentage === 100 })}
							>
								100%
							</button>
						</div>
						<FlexBlock columnGap="20px" className={styles.safPercentageWrapper}>
							<input
								type="number"
								min="0"
								max="100"
								value={safCostPercentage}
								onChange={e => {
									const raw = e.target.value;
									const normalized = raw.replace(/^0+/, '');
									const num = clamp(Number(normalized), 0, 100);
									setSafCostPercentage(num);
								}}
								placeholder="Custom SAF percentage"
								className={styles.customSafPercentage}
							/>
							<FlexBlock className={styles.inputStepper}>
								<button
									onClick={() => safCostPercentage > 0 && setSafCostPercentage(safCostPercentage - 1)}
									disabled={safCostPercentage === 0}
								>
									<MinusIcon />
								</button>
								<button
									onClick={() =>
										safCostPercentage !== 100 && setSafCostPercentage(safCostPercentage + 1)
									}
									disabled={safCostPercentage === 100}
								>
									<PlusIcon />
								</button>
							</FlexBlock>
						</FlexBlock>
					</div>
				</div>
				<FlexBlock flexDirection="column" rowGap="30px" className={styles.resultsWrapper}>
					<FlexBlock flexDirection="column" rowGap="16px" className={styles.results}>
						<FlexBlock
							flexDirection="column"
							justifyContent="space-between"
							rowGap="20px"
							className={styles.result}
						>
							<p className={styles.desc}>Per trip emission</p>
							<FlexBlock
								justifyContent="space-between"
								alignItems="flex-end"
								columnGap="10px"
								className={styles.amount}
							>
								<Animated
									key={`c-${seed}`}
									end={carbonQuantity / 1000}
									widthCh={finalCarbonStr.length}
									decimals={1}
								/>
								<p>tCO2</p>
							</FlexBlock>
						</FlexBlock>
						<FlexBlock
							flexDirection="column"
							justifyContent="space-between"
							rowGap="20px"
							className={styles.result}
						>
							<p className={styles.desc}>Annual footprint</p>
							<FlexBlock
								justifyContent="space-between"
								alignItems="flex-end"
								columnGap="10px"
								className={styles.amount}
							>
								<Animated key={`y-${seed}`} end={yearlyEmission} widthCh={finalYearlyStr.length} />
								<p>tCO2</p>
							</FlexBlock>
						</FlexBlock>
						<FlexBlock
							flexDirection="column"
							justifyContent="space-between"
							rowGap="20px"
							className={styles.result}
						>
							<p className={styles.desc}>Total Sustainable Aviation Fuel cost per year</p>
							<FlexBlock
								justifyContent="space-between"
								alignItems="flex-end"
								columnGap="10px"
								className={styles.amount}
							>
								<Animated key={`s-${seed}`} end={safCost} widthCh={finalSafStr.length} />
								<p>USD</p>
							</FlexBlock>
						</FlexBlock>
						<p className={styles.methodology}>
							Emissions data by{' '}
							<Link to={externalLinks.squakeWebsite} target="_blank">
								Squake
							</Link>{' '}
							(DEFRA methodology).
						</p>
					</FlexBlock>
					{isSuccess && data?.carbonQuantity && (
						<div className={styles.shareResults}>
							<p className={styles.subheader}>Want to share your results?</p>
							<FlexBlock columnGap="2px" className={styles.link}>
								<div className={styles.linkPreview}>
									<p>{linkToCalculator}</p>
								</div>
								{!showSuccessCheckmark && (
									<button onClick={copyToClipboard}>
										<CopyIcon />
									</button>
								)}
								{showSuccessCheckmark && (
									<div className={styles.checkmarkIcon}>
										<CheckIcon />
									</div>
								)}
							</FlexBlock>
						</div>
					)}
				</FlexBlock>
			</FlexBlock>
		</div>
	);
};

export default EmissionCalculator;
